import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  keyframes,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useThemeMode } from '../context/ThemeContext';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const softPulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

function Login({ onLogin }) {
  const { mode, toggleMode } = useThemeMode();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const userIdRef = useRef(null);
  const passwordRef = useRef(null);
  const isDark = mode === 'dark';

  useEffect(() => {
    const onBlur = () => setCapsLockOn(false);
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, []);

  const handlePasswordKeyEvent = (e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const id = userId.trim();
    const pwd = password;
    if (!id || !pwd) {
      setError('Enter your User ID and password');
      if (!id) userIdRef.current?.focus();
      else passwordRef.current?.focus();
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const ok = await onLogin(id, pwd);
      if (ok) return;
      setError('Invalid User ID or password');
      setPassword('');
      requestAnimationFrame(() => passwordRef.current?.focus());
    } catch (err) {
      setError(err?.message || 'Could not sign in. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(userId.trim() && password) && !submitting;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: 2,
        py: 4,
        background: isDark
          ? `
            radial-gradient(ellipse 70% 50% at 15% 85%, rgba(230,0,18,0.28) 0%, transparent 55%),
            radial-gradient(ellipse 55% 45% at 90% 10%, rgba(255,140,0,0.18) 0%, transparent 50%),
            linear-gradient(165deg, #0D0D0D 0%, #1A1A1A 50%, #121212 100%)
          `
          : `
            radial-gradient(ellipse 70% 50% at 10% 90%, rgba(230,0,18,0.12) 0%, transparent 55%),
            radial-gradient(ellipse 55% 45% at 95% 5%, rgba(255,160,40,0.14) 0%, transparent 50%),
            linear-gradient(160deg, #F8F4F0 0%, #F2EDE8 45%, #EDE6DE 100%)
          `,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: { xs: 260, md: 420 },
          height: { xs: 260, md: 420 },
          borderRadius: '50%',
          top: { xs: -80, md: -100 },
          right: { xs: -60, md: -80 },
          background: isDark
            ? 'radial-gradient(circle, rgba(230,0,18,0.22) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(230,0,18,0.14) 0%, transparent 70%)',
          animation: `${softPulse} 8s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: { xs: 200, md: 320 },
          height: { xs: 200, md: 320 },
          borderRadius: '50%',
          bottom: { xs: -60, md: -80 },
          left: { xs: -40, md: -60 },
          background: isDark
            ? 'radial-gradient(circle, rgba(255,160,40,0.16) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(255,160,40,0.18) 0%, transparent 70%)',
          animation: `${softPulse} 9s ease-in-out 1s infinite`,
          pointerEvents: 'none',
        }}
      />

      <IconButton
        onClick={toggleMode}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 2,
          color: 'text.secondary',
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.95)',
          },
        }}
      >
        {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>

      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          animation: `${fadeUp} 0.5s ease-out both`,
          bgcolor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
          boxShadow: isDark
            ? '0 24px 56px rgba(0,0,0,0.5)'
            : '0 24px 56px rgba(26,26,26,0.1)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Box
            component="img"
            src={LOGO_SRC}
            alt="Canteeny"
            sx={{
              width: 72,
              height: 72,
              mb: 1.5,
              filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.18))',
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.04em',
              fontSize: '1.85rem',
              lineHeight: 1.1,
              mb: 0.75,
            }}
          >
            Canteeny
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in with your staff credentials
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box aria-live="polite" sx={{ mb: error ? 2 : 0 }}>
            {error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            ) : null}
          </Box>

          <TextField
            fullWidth
            size="medium"
            label="User ID"
            name="username"
            autoComplete="username"
            value={userId}
            inputRef={userIdRef}
            onChange={(e) => {
              setUserId(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
            autoFocus
            disabled={submitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />

          <TextField
            fullWidth
            size="medium"
            label="Password"
            name="password"
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            inputRef={passwordRef}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handlePasswordKeyEvent}
            onKeyUp={handlePasswordKeyEvent}
            disabled={submitting}
            helperText={capsLockOn ? 'Caps Lock is on' : ' '}
            FormHelperTextProps={{
              sx: {
                color: capsLockOn ? 'warning.main' : 'transparent',
                m: 0,
                mt: 0.5,
                minHeight: 20,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    disabled={submitting}
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!canSubmit}
            sx={{
              mt: 1.5,
              py: 1.5,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 2,
              transition: 'transform 0.15s ease',
              '&:not(:disabled):hover': { transform: 'translateY(-1px)' },
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
                <CircularProgress size={18} color="inherit" thickness={5} />
                Signing in…
              </Box>
            ) : (
              'Sign in'
            )}
          </Button>
        </Box>
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ position: 'relative', zIndex: 1, mt: 3, opacity: 0.8 }}
      >
        Developed by Codewyn Dev
      </Typography>
    </Box>
  );
}

export default Login;
